import { useId, useRef } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

interface AgentSkillInstallDialogProps {
  open: boolean;
  onClose: () => void;
  onDownload: () => void;
  downloading: boolean;
}

export default function AgentSkillInstallDialog({
  open,
  onClose,
  onDownload,
  downloading,
}: AgentSkillInstallDialogProps) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth="lg"
      fullWidth
      TransitionProps={{ onEntered: () => closeButton.current?.focus() }}
    >
      <DialogTitle id={titleId}>Install the SOMA agent skill</DialogTitle>
      <DialogContent>
        <Box component="p" sx={{ mt: 0 }}>
          When the ZIP download finishes, follow these steps on the computer where you use your AI agent.
        </Box>
        <Box
          component="ol"
          sx={{ pl: 3, mb: 0, "& > li": { mb: 2 }, "& code": { overflowWrap: "anywhere" } }}
        >
          <li>
            <strong>Extract the ZIP.</strong> Keep the entire <code>soma</code> folder, including its
            references and tool folder. On Windows, right-click the ZIP in File Explorer and choose
            <strong> Extract All</strong> before moving the folder.
          </li>
          <li>
            <strong>Add it to your agent’s skills.</strong> Move the <code>soma</code> folder to the
            location for your agent:
            <Box component="ul" sx={{ pl: 2.5, my: 1 }}>
              <li>
                <strong>Windows</strong>
                <Box component="ul" sx={{ pl: 2.5 }}>
                  <li>
                    Codex: <code>%USERPROFILE%\.agents\skills\soma</code>
                  </li>
                  <li>
                    Claude Code: <code>%USERPROFILE%\.claude\skills\soma</code>
                  </li>
                </Box>
              </li>
              <li>
                <strong>macOS / Linux</strong>
                <Box component="ul" sx={{ pl: 2.5 }}>
                  <li>
                    Codex: <code>~/.agents/skills/soma</code>
                  </li>
                  <li>
                    Claude Code: <code>~/.claude/skills/soma</code>
                  </li>
                </Box>
              </li>
            </Box>
            On Windows, enter <code>%USERPROFILE%</code> in File Explorer’s address bar to open your user
            folder. On macOS/Linux, <code>~</code> means your user home folder. Create any missing
            folders. For other agents, follow their skill installation instructions. Start a new chat
            after installing. If your agent runs in WSL, use the Linux paths and set up the skill there.
          </li>
          <li>
            <strong>Ask your agent to finish setup.</strong> Send this message:
            <Box
              component="blockquote"
              sx={{
                mx: 0,
                my: 1,
                p: 1.5,
                borderLeft: "3px solid",
                borderColor: "primary.main",
                bgcolor: "action.hover",
              }}
            >
              Use the SOMA skill to check prerequisites, install the required .NET SDK and bundled
              soma-agent helper if needed, sign me in to SOMA, and show my accessible sites.
            </Box>
            <Box component="p" sx={{ my: 1 }}>
              Your agent will check for the .NET 10 SDK and install it when possible, or guide you
              through any setup it cannot complete.
            </Box>
            Complete sign-in in the browser when prompted. You do not need to share passwords or tokens
            with your agent.
          </li>
        </Box>
        <Button onClick={onDownload} disabled={downloading}>
          Download the ZIP again
        </Button>
      </DialogContent>
      <DialogActions>
        <Button ref={closeButton} onClick={onClose} variant="contained" autoFocus>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
}
